import React, {FC, lazy, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DecisionTreeModel} from '../../../../../libs/shared/core/src/models/api/decisionTreeModel';
import {deleteAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {logger} from '../../../../../libs/shared/core/src/services/logger/logger';
import {SettingsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dialog} from '../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogButtonGroup} from '../../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {DialogHeader} from '../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {useNavigationBlockingContext} from '../../../../../libs/shared/ui/src/components/navigation/navigationBlockingContext';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {wrapWithSuspense} from '../../../../helpers/suspenseWrapperHelpers';
import {SettingsBannersEnum, useSettingsBanners} from '../../../../settings/banners/settingsBannersContext';
import {SettingsChatBotFooter} from '../../../../settings/chatbots/settingsChatBotFooter';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {maxChatBotNodesCount} from '../constants';
import {useEditChatBotState} from '../hooks/useEditChatBotState';
import {ChatBotBuilderAppProps} from '../hooks/useFetchChatBotBuilderAppProps';
import {useUpdateSubtrees} from '../hooks/useUpdateSubtrees';
import {checkIsSubtreeSaveable} from '../validators';
import {ChatBotSubtreeNameDialog} from './chatBotSubtreeNameDialog';

const ChatBotBuilder = lazy(() =>
  // @ts-expect-error Bypass the TS error around wrong module type since the `import()` directive is handled by Webpack
  import(/* webpackChunkName: 'settingsChatBotBuilder', webpackPrefetch: true */ '../chatBotBuilder').then(
    (module) => ({
      default: module.ChatBotBuilder,
    }),
  ),
);

/*
 * Types.
 */

interface ChatBotSubtreeWrapperProps {
  panelId: SettingsPanelId;
  subtree: DecisionTreeModel;
  appProps: ChatBotBuilderAppProps;
  isMainTreeDirty: boolean;
  computeNumSubtreeUses: (subtree?: DecisionTreeModel) => number;
  cancelNavigationMainTree: () => void;
  unblockNavigationMainTree: () => void;
  onSubtreeSelected: (arg: DecisionTreeModel | undefined) => void;
  onSubtreeDeleted: () => void;
}

/*
 * Styles.
 */

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 48px;
`;

const StyledHeaderContentDiv = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 48px;
  align-items: center;
`;

const StyledSubtreeTitleDiv = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
`;

const StyledVerticalRuleDiv = styled.div`
  border-left: 1px solid ${(p) => p.theme.greys.shade40};
  height: 30px;
`;

const StyledDeleteSubtreeTextDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
`;

/*
 * Component.
 */

/** This component computes the store for a selected subtree and then renders the SubtreeBuilder. */
export const ChatBotSubtreeWrapper: FC<ChatBotSubtreeWrapperProps> = ({
  panelId,
  subtree,
  appProps,
  computeNumSubtreeUses,
  isMainTreeDirty,
  cancelNavigationMainTree,
  unblockNavigationMainTree,
  onSubtreeSelected,
  onSubtreeDeleted,
}) => {
  const {store: chatBotBuilderStore, isDirty, chatBot, onSave, onCancel} = useEditChatBotState(subtree.id);
  const theme = useUserTheme();
  const companyUrl = useAppSelector(getCompanyUrl);
  const [isShowingRenameDialog, setIsShowingRenameDialog] = useState(false);
  const [isShowingDeleteDialog, setIsShowingDeleteDialog] = useState(false);
  const [isGoingBackToMainFlow, setIsGoingBackToMainFlow] = useState(false);
  const {showBanner} = useSettingsBanners();
  const {useNavigationBlocking} = useNavigationBlockingContext();
  const {didBlockNavigation, unblockNavigation, cancelNavigation} = useNavigationBlocking(
    panelId,
    isDirty || isMainTreeDirty,
  );

  const hasNodesWithErrors = chatBotBuilderStore.nodeIdsWithErrors.length > 0;
  const isSaveDisabled =
    !checkIsSubtreeSaveable(chatBotBuilderStore.nodes, chatBotBuilderStore.edges) || hasNodesWithErrors;
  const isShowingFooter = isDirty && !chatBotBuilderStore.selectedNode;

  const deleteSubtree = async () => {
    try {
      await deleteAsync(`${companyUrl}/decision_trees/${subtree.id}`);
      onSubtreeDeleted();
      onSubtreeSelected(undefined);
    } catch (err) {
      if (err.message) {
        logger.error(err.message, err.data);
      }
      const bannerMessage = err.reason ?? (
        <Trans id="settings-chat-bots-subtree-delete-error">Error deleting subtree</Trans>
      );
      showBanner({type: SettingsBannersEnum.ERROR, message: bannerMessage});
    }
  };

  const {updateSubtreeMutation, deleteSubtreeMutation} = useUpdateSubtrees(subtree, onSave, deleteSubtree);

  const onGoBackToMainFlow = () => {
    onCancel();
    onSubtreeSelected(undefined);
    // if we are going back to the main flow, cancel anything we were navigating to
    cancelNavigationMainTree();
  };

  const onUnsavedDialogDiscardChanges = () => {
    if (isGoingBackToMainFlow) {
      setIsGoingBackToMainFlow(false);
      onGoBackToMainFlow();
      return;
    }
    // if we are discarding all changes, unblock the navigation for the subtree and main tree
    unblockNavigation();
    unblockNavigationMainTree();
  };

  const onUnsavedDialogCancelClick = () => {
    setIsGoingBackToMainFlow(false);
    cancelNavigation();
  };

  if (!chatBot) {
    return null;
  }

  return (
    <>
      <StyledHeader>
        <StyledHeaderContentDiv>
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            leftIcon="chevronLeftSmall"
            onClick={() => {
              if (isDirty) {
                setIsGoingBackToMainFlow(true);
                return;
              }
              onGoBackToMainFlow();
            }}
          >
            <Trans id="chatbot-subtree-main-flow-button">Back to main flow</Trans>
          </Button>
          <StyledVerticalRuleDiv />
          <StyledSubtreeTitleDiv>{chatBot.name}</StyledSubtreeTitleDiv>
        </StyledHeaderContentDiv>
        <PopoverCoordinator
          renderButton={() => (
            <IconButton
              iconName="moreVertical"
              onClick={() => {}}
              label={<Trans id="chatbot-subtree-more-button">More menu</Trans>}
              color={theme.greys.shade80}
            />
          )}
          renderModal={(popoverProps) => (
            <SimpleDropdown {...popoverProps} placement="bottom-end">
              <SimpleDropdownItem
                onSelect={() => {
                  setIsShowingRenameDialog(true);
                }}
              >
                <Trans id="chatbot-subtree-more-button-rename">Rename saved flow</Trans>
              </SimpleDropdownItem>
              <SimpleDropdownItem onSelect={() => setIsShowingDeleteDialog(true)}>
                <StyledDeleteSubtreeTextDiv>
                  <Trans id="chatbot-subtree-more-button-delete">Delete saved flow</Trans>
                </StyledDeleteSubtreeTextDiv>
              </SimpleDropdownItem>
            </SimpleDropdown>
          )}
        />
      </StyledHeader>

      {wrapWithSuspense(
        <ChatBotBuilder
          store={chatBotBuilderStore}
          appProps={appProps}
          onResetChatbotFlow={() => null}
          computeNumSubtreeUses={computeNumSubtreeUses}
          onSubtreeSelected={(newSubtree) => {
            onCancel();
            onSubtreeSelected(newSubtree);
          }}
          selectedSubtree={subtree}
        />,
      )}
      {isShowingFooter && (
        <SettingsChatBotFooter
          isSaveDisabled={isSaveDisabled}
          renderDisabledSaveTooltip={() =>
            renderSaveDisabledTooltip(
              hasNodesWithErrors,
              chatBotBuilderStore.nodes.length > maxChatBotNodesCount,
            )
          }
          onCancelClick={onCancel}
          onSaveClick={() => updateSubtreeMutation.mutate(undefined)}
        />
      )}
      {isShowingRenameDialog && (
        <ChatBotSubtreeNameDialog
          subtrees={appProps.subtrees}
          title={<Trans id="chatbot-subtree-rename-dialog-title">Rename saved flow</Trans>}
          onCancelClick={() => setIsShowingRenameDialog(false)}
          okButtonLabel={<Trans id="chatbot-subtree-rename-dialog-button">Rename</Trans>}
          onOkClick={async (name) => {
            updateSubtreeMutation.mutate(name.trim());
            setIsShowingRenameDialog(false);
          }}
        />
      )}
      {isShowingDeleteDialog && (
        <OkCancelDialog
          okButtonVariant={ButtonVariantsEnum.DANGER}
          title={<Trans id="chat-bot-subtree-delete-dialog-title">Delete saved flow</Trans>}
          okButtonLabel={<Trans id="chat-bot-subtree-delete-dialog-confirm">Delete</Trans>}
          onRequestClose={() => setIsShowingDeleteDialog(false)}
          onOkClick={() => deleteSubtreeMutation.mutate()}
        >
          <Trans id="chat-bot-subtree-delete-dialog-content">
            Deleting this saved flow “{subtree.name}” will delete all saved flow steps using this saved flow.
          </Trans>
          <br />
          <br />
          <Plural
            id="chat-bot-subtree-delete-dialog-content-plural"
            value={computeNumSubtreeUses(subtree)}
            one="1 step in
            the main chatbot flow will be deleted."
            other="# steps in
            the main chatbot flow will be deleted."
          />
        </OkCancelDialog>
      )}
      {renderNavigationBlockDialog(
        isDirty,
        isMainTreeDirty,
        didBlockNavigation,
        isGoingBackToMainFlow,
        onGoBackToMainFlow,
        onUnsavedDialogDiscardChanges,
        onUnsavedDialogCancelClick,
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderNavigationBlockDialog(
  isDirty: boolean,
  isMainTreeDirty: boolean,
  didBlockNavigation: boolean,
  isGoingBackToMainFlow: boolean,
  onGoBackToMainFlow: () => void,
  onDiscardClick: () => void,
  onCancelClick: () => void,
) {
  if (!didBlockNavigation && !isGoingBackToMainFlow) {
    return null;
  }

  const renderMessage = () => {
    if (isGoingBackToMainFlow && isDirty) {
      return (
        <Trans id="chat-bot-subtree-current-flow-unsaved-changes-dialog-content">
          This flow has unsaved changes. Are you sure you want to discard them?
        </Trans>
      );
    }

    if (isMainTreeDirty && isDirty) {
      return (
        <Trans id="chat-bot-subtree-both-flows-unsaved-changes-dialog-content">
          Your flows have unsaved changes. Are you sure you want to discard them?
        </Trans>
      );
    }

    if (isMainTreeDirty) {
      return (
        <Trans id="chat-bot-subtree-main-tree-unsaved-changes-dialog-content">
          The main flow has unsaved changes. Are you sure you want to discard them?
        </Trans>
      );
    }

    return (
      <Trans id="chat-bot-subtree-unsaved-changes-dialog-content">
        Your flows have unsaved changes. Are you sure you want to discard them?
      </Trans>
    );
  };

  return (
    <Dialog>
      <DialogHeader
        title={<Trans id="chat-bot-subtree-unsaved-changes-dialog-title">Unsaved changes</Trans>}
      />
      <DialogContent>{renderMessage()}</DialogContent>
      <DialogButtonGroup>
        <ButtonGroupSpacer />
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
          <Trans id="chat-bot-subtree-unsaved-changes-dialog-cancel-button">Cancel</Trans>
        </Button>
        {isMainTreeDirty && !isDirty && (
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onGoBackToMainFlow}>
            <Trans id="chat-bot-subtree-unsaved-changes-dialog-main-flow-button">Back to main flow</Trans>
          </Button>
        )}
        <Button variant={ButtonVariantsEnum.DANGER} onClick={onDiscardClick}>
          <Trans id="chat-bot-subtree-unsaved-changes-dialog-discard-button">Discard changes</Trans>
        </Button>
      </DialogButtonGroup>
    </Dialog>
  );
}

function renderSaveDisabledTooltip(hasNodesWithErrors: boolean, maxNodesReached: boolean) {
  if (hasNodesWithErrors) {
    return <Trans id="chatbot-subtree-footer-save-tooltip-invalid-steps">Resolve any invalid steps.</Trans>;
  }
  if (maxNodesReached) {
    return (
      <Trans id="chatbot-subtree-footer-save-tooltip-invalid-steps">
        Cannot have more than {maxChatBotNodesCount} nodes.
      </Trans>
    );
  }
  return null;
}
